/* eslint-disable tailwindcss/no-custom-classname */

import IconStatusDot from '@apollo/icons/default/IconStatusDot.svg';
import {
  PropsOf,
  chakra,
  forwardRef,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import classnames from 'classnames';
import React, { ReactElement } from 'react';

import { useDevWarning } from '../../hooks/useDevWarning';
import { OmitChakraProps } from '../../utils/typeUtils';

export interface StatusBadgeProps
  extends OmitChakraProps<PropsOf<'span'>, 'children'> {
  icon?: ReactElement;
  colorScheme?:
    | 'yellow'
    | 'gray'
    | 'green'
    | 'purple'
    | 'blue'
    | 'red'
    | 'navy';
  children: string;
  variant?: 'hidden' | 'rounded';
}

export const StatusBadge = forwardRef<StatusBadgeProps, 'span'>(
  ({ colorScheme = 'gray', icon, ...props }, ref) => {
    const styles = useMultiStyleConfig('StatusBadge', props);
    const { className, children, ...rest } = props;
    useDevWarning(
      'StatusBadge',
      typeof children === 'string' ? children.toUpperCase() !== children : true,
      'Use sentence-casing instead of all caps for the text.',
    );

    return (
      <chakra.span
        ref={ref}
        className={classnames('status-badge', className)}
        {...rest}
        __css={styles.container}
      >
        <StatusBadgeIcon icon={icon} colorScheme={colorScheme} />
        {children}
      </chakra.span>
    );
  },
);

interface StatusBadgeIconProps {
  icon?: ReactElement;
  colorScheme: StatusBadgeProps['colorScheme'];
}

const StatusBadgeIcon = ({ icon, colorScheme }: StatusBadgeIconProps) => {
  const styles = useMultiStyleConfig('StatusBadge', { colorScheme });

  return (
    <chakra.span __css={styles.icon}>{icon || <IconStatusDot />}</chakra.span>
  );
};
